import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatCurrency } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  Plus, Search, Users, ArrowRight, Calendar,
  Loader2, Filter, SquarePen, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { handleLocalizedInvalid, clearLocalizedInvalid } from '../lib/localizedValidation';

export const TontinesPage = () => {
  const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100', 'all'];
  const ALL_FETCH_CHUNK = 50;
  const SEARCH_DEBOUNCE_MS = 300;
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tontines, setTontines] = useState([]);
  const [totalTontines, setTotalTontines] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [selectedTontine, setSelectedTontine] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  useEffect(() => {
    if (searchParams.get('focus') === 'join') {
      setShowJoinDialog(true);
    }
  }, [searchParams]);

  useEffect(() => {
    loadTontines();
  }, [currentPage, debouncedSearchQuery, pageSize]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const loadTontines = async () => {
    try {
      const isAllMode = pageSize === 'all';
      if (isAllMode) {
        let offset = 0;
        let total = 0;
        let collected = [];

        do {
          const response = await api.getTontinesPaginated(ALL_FETCH_CHUNK, offset, debouncedSearchQuery);
          const items = response.data?.items || [];
          total = response.data?.total || items.length;
          collected = [...collected, ...items];
          offset += ALL_FETCH_CHUNK;

          if (items.length === 0) {
            break;
          }
        } while (collected.length < total);

        setTontines(collected);
        setTotalTontines(total);
        return;
      }

      const numericPageSize = Number(pageSize);
      const offset = (currentPage - 1) * numericPageSize;
      const response = await api.getTontinesPaginated(numericPageSize, offset, debouncedSearchQuery);
      setTontines(response.data?.items || []);
      setTotalTontines(response.data?.total || 0);
    } catch (error) {
      console.error('Error loading tontines:', error);
    } finally {
      setLoading(false);
    }
  };

  const pageSizeValue = pageSize === 'all' ? Math.max(totalTontines, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalTontines / pageSizeValue));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getTypeLabel = (type) => t(`tontine.types.${type}`);
  const getFrequencyLabel = (frequency) => t(`tontine.frequencies.${frequency}`);

  const openEditDialog = (event, tontine) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedTontine(tontine);
    setShowEditDialog(true);
  };

  const openArchiveDialog = (event, tontine) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedTontine(tontine);
    setShowArchiveDialog(true);
  };

  const handleArchive = async () => {
    if (!selectedTontine) return;
    setArchiveLoading(true);
    try {
      await api.deleteTontine(selectedTontine.id);
      toast.success('Tontine archivée avec succès');
      setShowArchiveDialog(false);
      setSelectedTontine(null);
      await loadTontines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l’archivage');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleJoinByCode = () => {
    const normalizedCode = inviteCode.trim();
    if (!normalizedCode) {
      toast.error(t('validation.required'));
      return;
    }
    setShowJoinDialog(false);
    navigate(`/join/${encodeURIComponent(normalizedCode)}`);
  };

  return (
    <div className="space-y-6" data-testid="tontines-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">
            {t('tontinesPage.title')}
          </h1>
          <p className="text-stone-600 dark:text-stone-400 mt-1">
            {t('tontinesPage.subtitle')}
          </p>
        </div>
        <div className="flex w-full sm:w-auto gap-2">
          <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 w-full sm:w-auto" data-testid="join-tontine-btn">
                <Users className="w-4 h-4" />
                {t('tontine.join')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{t('tontine.join')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Label htmlFor="join-invite-code">{t('invite.code')}</Label>
                <Input
                  id="join-invite-code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="ABCD1234"
                  data-testid="join-invite-code-input"
                />
                <Button className="w-full btn-accent" onClick={handleJoinByCode} data-testid="join-submit-btn">
                  {t('joinPage.joinButton')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-accent gap-2 w-full sm:w-auto" data-testid="create-tontine-btn">
                <Plus className="w-4 h-4" />
                {t('tontinesPage.newTontine')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{t('tontine.create')}</DialogTitle>
              </DialogHeader>
              <CreateTontineForm 
                onSuccess={() => {
                  setShowCreateDialog(false);
                  loadTontines();
                }} 
                t={t}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder={t('tontinesPage.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
          data-testid="search-tontines-input"
        />
      </div>

      {/* Tontines Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      ) : totalTontines === 0 ? (
        <Card className="card-neo">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-stone-100 dark:bg-stone-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="font-display text-lg font-semibold text-stone-900 dark:text-stone-50 mb-2">
              {searchQuery ? t('tontinesPage.empty.noResults') : t('tontinesPage.empty.noTontine')}
            </h3>
            <p className="text-stone-500 dark:text-stone-400 mb-6 max-w-sm mx-auto">
              {searchQuery 
                ? t('tontinesPage.empty.tryOtherSearch')
                : t('tontinesPage.empty.createFirst')
              }
            </p>
            {!searchQuery && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowJoinDialog(true)}
                  data-testid="empty-join-tontine-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {t('tontine.join')}
                </Button>
                <Button 
                  className="btn-accent"
                  onClick={() => setShowCreateDialog(true)}
                  data-testid="empty-create-tontine-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('tontine.create')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tontines.map((tontine) => (
              <Card key={tontine.id} className="card-neo h-full hover:-translate-y-1 transition-transform">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                        >
                          {getTypeLabel(tontine.type)}
                        </Badge>
                        {tontine.admin_id === user?.id && (
                          <Badge
                            variant="outline"
                            className="text-xs border-orange-200 text-orange-700"
                            data-testid={`list-admin-badge-${tontine.id}`}
                            title="Vous êtes admin de cette tontine"
                          >
                            {t('tontine.admin')}
                          </Badge>
                        )}
                        <Badge 
                          className={
                            tontine.status === 'active' 
                              ? 'bg-green-100 text-green-700 hover:bg-green-100' 
                              : 'bg-stone-100 text-stone-600'
                          }
                        >
                          {tontine.status === 'active' ? t('tontine.statuses.active') : tontine.status}
                        </Badge>
                      </div>
                      <h3 className="font-display font-bold text-lg text-stone-900 dark:text-stone-50 line-clamp-2 break-words pr-2">
                        {tontine.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 pl-2">
                      {tontine.admin_id === user?.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(event) => openEditDialog(event, tontine)}
                            data-testid={`list-edit-tontine-btn-${tontine.id}`}
                          >
                            <SquarePen className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={(event) => openArchiveDialog(event, tontine)}
                            data-testid={`list-archive-tontine-btn-${tontine.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {tontine.description && (
                    <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 line-clamp-2">
                      {tontine.description}
                    </p>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">{t('tontine.contribution')}</span>
                      <span className="font-semibold text-stone-900 dark:text-stone-50 tabular-nums">
                        {formatCurrency(tontine.contribution_amount, tontine.currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">{t('tontine.frequency')}</span>
                      <span className="text-stone-700 dark:text-stone-300">
                        {getFrequencyLabel(tontine.frequency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">{t('tontine.members')}</span>
                      <span className="text-stone-700 dark:text-stone-300">
                        {tontine.current_members}/{tontine.max_members}
                      </span>
                    </div>
                    <Progress 
                      value={(tontine.current_members / tontine.max_members) * 100} 
                      className="h-1.5"
                    />
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-100 dark:border-stone-800">
                    <span className="text-xs text-stone-500">
                      {t('tontinesPage.roundLabel', { round: tontine.current_round })}
                    </span>
                    <Link to={`/tontines/${tontine.id}`} data-testid={`tontine-item-${tontine.id}`}>
                      <span className="text-sm text-orange-600 flex items-center gap-1">
                        {t('tontinesPage.viewDetails')} <ArrowRight className="w-3 h-3" />
                      </span>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {totalTontines > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2" data-testid="tontines-pagination">
              <span className="text-sm text-stone-500" data-testid="tontines-page-indicator">
                {t('common.page')} {currentPage} / {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger className="w-[120px] h-8" data-testid="tontines-page-size-select">
                    <SelectValue placeholder={t('common.pageSize')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option === 'all' ? t('common.all') : option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1 || pageSize === 'all'}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  data-testid="tontines-page-prev"
                >
                  {t('common.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages || pageSize === 'all'}
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  data-testid="tontines-page-next"
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Modifier la tontine</DialogTitle>
          </DialogHeader>
          <EditTontineForm
            tontine={selectedTontine}
            onSuccess={async () => {
              setShowEditDialog(false);
              setSelectedTontine(null);
              await loadTontines();
              toast.success(t('settings.saved'));
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Supprimer la tontine ?</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)} data-testid="list-archive-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleArchive} disabled={archiveLoading} data-testid="list-archive-confirm-btn">
              {archiveLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('common.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EditTontineForm = ({ tontine, onSuccess }) => {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: tontine?.name || '',
    description: tontine?.description || '',
    contribution_amount: String(tontine?.contribution_amount || ''),
    frequency: tontine?.frequency || 'monthly',
    currency: tontine?.currency || 'XOF',
    max_members: String(tontine?.max_members || 10),
    rules: tontine?.rules || '',
  });

  useEffect(() => {
    if (!tontine) return;
    setFormData({
      name: tontine.name || '',
      description: tontine.description || '',
      contribution_amount: String(tontine.contribution_amount || ''),
      frequency: tontine.frequency || 'monthly',
      currency: tontine.currency || 'XOF',
      max_members: String(tontine.max_members || 10),
      rules: tontine.rules || '',
    });
  }, [tontine]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tontine?.id) return;
    setSaving(true);
    try {
      await api.updateTontine(tontine.id, {
        name: formData.name,
        description: formData.description,
        contribution_amount: parseFloat(formData.contribution_amount),
        frequency: formData.frequency,
        currency: formData.currency,
        max_members: parseInt(formData.max_members, 10),
        rules: formData.rules,
      });
      await onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
      onInputCapture={clearLocalizedInvalid}
      onChangeCapture={clearLocalizedInvalid}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="list-edit-name">{t('tontine.name')} *</Label>
        <Input
          id="list-edit-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="list-edit-tontine-name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="list-edit-description">{t('tontine.description')}</Label>
        <Textarea
          id="list-edit-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('tontine.frequency')}</Label>
          <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
            <SelectTrigger data-testid="list-edit-tontine-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">{t('tontine.frequencies.weekly')}</SelectItem>
              <SelectItem value="biweekly">{t('tontine.frequencies.biweekly')}</SelectItem>
              <SelectItem value="monthly">{t('tontine.frequencies.monthly')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('tontine.currency')}</Label>
          <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
            <SelectTrigger data-testid="list-edit-tontine-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="XOF">XOF (FCFA)</SelectItem>
              <SelectItem value="CAD">CAD ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="list-edit-amount">{t('tontine.contribution')} *</Label>
          <Input
            id="list-edit-amount"
            type="number"
            value={formData.contribution_amount}
            onChange={(e) => setFormData({ ...formData, contribution_amount: e.target.value })}
            required
            min="1"
            data-testid="list-edit-tontine-amount"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="list-edit-max-members">{t('tontine.maxMembers')} *</Label>
          <Input
            id="list-edit-max-members"
            type="number"
            value={formData.max_members}
            onChange={(e) => setFormData({ ...formData, max_members: e.target.value })}
            required
            min="2"
            max="200"
            data-testid="list-edit-tontine-max-members"
          />
        </div>
      </div>

      <Button type="submit" className="w-full btn-accent" disabled={saving} data-testid="list-edit-tontine-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SquarePen className="w-4 h-4 mr-2" />}
        {t('common.save')}
      </Button>
    </form>
  );
};

const CreateTontineForm = ({ onSuccess, t }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'rosca',
    contribution_amount: '',
    frequency: 'monthly',
    currency: 'XOF',
    max_members: '10',
    rules: '',
  });
  const isFlexibleType = formData.type === 'flex_asca';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = parseFloat(formData.contribution_amount);
    const resolvedAmount = isFlexibleType
      ? (Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : 0)
      : parsedAmount;

    if (!isFlexibleType && (!Number.isFinite(resolvedAmount) || resolvedAmount <= 0)) {
      toast.error('Le montant de cotisation est obligatoire et doit être supérieur à 0');
      return;
    }

    setLoading(true);
    try {
      await api.createTontine({
        ...formData,
        contribution_amount: resolvedAmount,
        max_members: parseInt(formData.max_members),
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating tontine:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
      onInputCapture={clearLocalizedInvalid}
      onChangeCapture={clearLocalizedInvalid}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="name">{t('tontine.name')} *</Label>
        <Input
          id="name"
          placeholder={t('tontinesPage.form.namePlaceholder')}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="create-tontine-name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">{t('tontine.description')}</Label>
        <Textarea
          id="description"
          placeholder={t('tontinesPage.form.descriptionPlaceholder')}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
          data-testid="create-tontine-description"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('tontine.type')}</Label>
          <Select 
            value={formData.type} 
            onValueChange={(v) => setFormData({ ...formData, type: v })}
          >
            <SelectTrigger data-testid="create-tontine-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rosca">{t('tontine.types.rosca')}</SelectItem>
              <SelectItem value="asca">{t('tontine.types.asca')}</SelectItem>
              <SelectItem value="flex_asca">{t('tontine.types.flex_asca')}</SelectItem>
              <SelectItem value="hybrid">{t('tontine.types.hybrid')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('tontine.frequency')}</Label>
          <Select 
            value={formData.frequency} 
            onValueChange={(v) => setFormData({ ...formData, frequency: v })}
          >
            <SelectTrigger data-testid="create-tontine-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">{t('tontine.frequencies.weekly')}</SelectItem>
              <SelectItem value="biweekly">{t('tontine.frequencies.biweekly')}</SelectItem>
              <SelectItem value="monthly">{t('tontine.frequencies.monthly')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">{t('tontinesPage.form.contributionAmount')}{isFlexibleType ? '' : ' *'}</Label>
          <Input
            id="amount"
            type="number"
            placeholder="50000"
            value={formData.contribution_amount}
            onChange={(e) => setFormData({ ...formData, contribution_amount: e.target.value })}
            required={!isFlexibleType}
            min={isFlexibleType ? '0' : '1'}
            data-testid="create-tontine-amount"
          />
          {isFlexibleType && (
            <p className="text-xs text-stone-500">Montant de référence optionnel pour l’épargne libre.</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('tontine.currency')}</Label>
          <Select 
            value={formData.currency} 
            onValueChange={(v) => setFormData({ ...formData, currency: v })}
          >
            <SelectTrigger data-testid="create-tontine-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="XOF">XOF (FCFA)</SelectItem>
              <SelectItem value="CAD">CAD ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="members">{t('tontine.maxMembers')}</Label>
        <Input
          id="members"
          type="number"
          placeholder="10"
          value={formData.max_members}
          onChange={(e) => setFormData({ ...formData, max_members: e.target.value })}
          min="2"
          max="50"
          data-testid="create-tontine-max-members"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full btn-accent" 
        disabled={loading}
        data-testid="create-tontine-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {t('tontinesPage.form.createButton')}
      </Button>
    </form>
  );
};

export default TontinesPage;
